import { useState } from "react";
import { Button } from "@/components/ui/button";
import { usePrefersReducedMotion } from "./useMediaMatch";

/**
 * FeatureSpotlightCard: o conteúdo do cartão de novidade.
 *
 * Só apresentação. Quem posiciona (ancorado ao item de menu no desktop, fixo no
 * rodapé no celular) é o `FeatureSpotlightHost`; o admin reaproveita este mesmo
 * componente na pré-visualização e em "Testar na minha conta", então o que ele vê
 * é o que a pessoa recebe.
 *
 * Mídia: imagem/GIF em 16:9; vídeo mudo, em laço, inline e sem controles. Com
 * preferência por movimento reduzido o vídeo não toca sozinho e mostra o primeiro
 * quadro. Se a mídia falhar, a área some e o resto do cartão segue funcionando.
 *
 * @param {{
 *   title: string,
 *   body: string,
 *   mediaUrl?: string|null,
 *   mediaType?: "image"|"video"|null,
 *   ctaLabel?: string,
 *   onDismiss: () => void,
 *   onTry: () => void,
 *   titleId?: string,
 * }} props
 */
export default function FeatureSpotlightCard({
  title,
  body,
  mediaUrl = null,
  mediaType = null,
  ctaLabel = "Experimente",
  onDismiss,
  onTry,
  titleId,
}) {
  // A falha vale para a URL que falhou: ao trocar a mídia (pré-visualização do admin) ela tenta de novo.
  const [failedUrl, setFailedUrl] = useState(null);
  const showMedia = Boolean(mediaUrl) && failedUrl !== mediaUrl;

  return (
    <div className="overflow-hidden rounded-lg">
      {showMedia && (
        <div className="aspect-video w-full overflow-hidden bg-surface">
          <CardMedia url={mediaUrl} type={mediaType} onError={() => setFailedUrl(mediaUrl)} />
        </div>
      )}
      <div className="space-y-1.5 p-4">
        <h2 id={titleId} className="text-sm font-semibold leading-snug text-foreground-strong">
          {title}
        </h2>
        <p className="text-sm leading-relaxed text-foreground-muted">{body}</p>
      </div>
      <div className="flex items-center justify-end gap-2 px-4 pb-4">
        <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
          Fechar
        </Button>
        <Button type="button" size="sm" onClick={onTry}>
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}

function CardMedia({ url, type, onError }) {
  const reducedMotion = usePrefersReducedMotion();

  if (type === "video") {
    // `#t=0.001` faz o Safari pintar o primeiro quadro quando o vídeo não toca sozinho.
    const src = reducedMotion && !url.includes("#") ? `${url}#t=0.001` : url;
    return (
      <video
        src={src}
        className="h-full w-full object-cover"
        muted
        loop
        playsInline
        autoPlay={!reducedMotion}
        preload="metadata"
        aria-hidden="true"
        onError={onError}
      />
    );
  }

  return <img src={url} alt="" className="h-full w-full object-cover" onError={onError} />;
}
